import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Divider,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import {
  DownloadOutlined,
  EditOutlined,
  InboxOutlined,
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined,
  TeamOutlined,
  SafetyOutlined,
  EyeOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import {
  CatalogFieldMeta,
  CatalogItem,
  createCatalogItem,
  getCatalog,
  getCatalogMapping,
  importCatalogCsv,
  importStockReport,
  updateCatalogItem,
} from "../services/catalogApi";
import { getStorages, Storage } from "../services/storageApi";
import { getRetailers, Retailer } from "../services/retailerApi";
import {
  getStockReportDataRows,
  ParsedStockReport,
  parseStockReportFile,
} from "../utils/stockReportParser";
import { useAppSelector } from "../store";
import {
  getAllInventoryLogs,
  InventoryLog,
} from "../services/inventoryService";

const { Title, Text } = Typography;
const { Dragger } = Upload;

const CATALOG_I18N = {
  fr: {
    title: "Catalogue",
    supplierSubtitle: "Vue en lecture seule de vos produits, stocks, états et lots.",
    internalSubtitle: "Gestion du référentiel produit, imports CSV et synchronisation omnicanale.",
    products: "Nombre de produits",
    lots: "Nombre de lots",
    catalogComplete: "Référentiel complet",
    marketableStockKpi: "Stock restant commercialisable",
    marketableStockSubtitle: "Quantité vendable disponible",
    brandCount: "Nombre de marques",
    brandDiversity: "Diversité catalogue",
    search: "Rechercher par produit, SKU ou EAN...",
    internalSearch: "Rechercher un produit, SKU, marque...",
    refresh: "Rafraîchir",
    selectedItems: "sélectionné(s)",
    importStock: "Importer un état de stock",
    inventoryLogs: "Logs d'inventaire",
    importCatalog: "Importer",
    importCatalogTitle: "Importer un catalogue",
    exportData: "Exporter",
    exportCatalogTitle: "Exporter le catalogue",
    addProduct: "Nouveau produit",
    inventoryLogsTitle: "Logs d'inventaire",
    importStockTitle: "Importer un état de stock",
    product: "Produit",
    brand: "Marque",
    stock: "Stock par entrepôt",
    stockShort: "Stock",
    totalStock: "Stock total",
    totalStockShort: "Total",
    status: "Statut",
    updated: "Mise à jour",
    marketable: "Commercialisable",
    reserved: "Réservé",
    defective: "Défectueux",
    sellableShort: "Com.",
    reservedShort: "Rés.",
    defectiveShort: "Déf.",
    lotDetails: "Détail des lots",
    lot: "Lot",
    expiry: "DLU",
    quantity: "Quantité",
    noLots: "Aucun lot renseigné",
  },
  kr: {
    title: "상품 카탈로그",
    supplierSubtitle: "상품, 창고별 재고, 상태 및 로트 정보를 조회합니다.",
    internalSubtitle: "상품 기준 정보, CSV 가져오기 및 옴니채널 동기화를 관리합니다.",
    products: "상품 수",
    lots: "로트 수",
    catalogComplete: "전체 기준 정보",
    marketableStockKpi: "판매 가능 잔여 재고",
    marketableStockSubtitle: "판매 가능한 수량",
    brandCount: "브랜드 수",
    brandDiversity: "카탈로그 다양성",
    search: "상품명, SKU 또는 EAN 검색...",
    internalSearch: "상품, SKU, 브랜드 검색...",
    refresh: "새로고침",
    selectedItems: "개 선택됨",
    importStock: "재고 현황 가져오기",
    inventoryLogs: "재고 로그",
    importCatalog: "가져오기",
    importCatalogTitle: "카탈로그 가져오기",
    exportData: "내보내기",
    exportCatalogTitle: "카탈로그 내보내기",
    addProduct: "신규 상품",
    inventoryLogsTitle: "재고 로그",
    importStockTitle: "재고 현황 가져오기",
    product: "상품",
    brand: "브랜드",
    stock: "창고별 재고",
    stockShort: "재고",
    totalStock: "총 재고",
    totalStockShort: "총계",
    status: "상태",
    updated: "업데이트",
    marketable: "판매 가능",
    reserved: "예약",
    defective: "불량",
    sellableShort: "판매",
    reservedShort: "예약",
    defectiveShort: "불량",
    lotDetails: "로트 상세",
    lot: "로트",
    expiry: "유통기한",
    quantity: "수량",
    noLots: "등록된 로트가 없습니다",
  },
};

const Catalog: React.FC = () => {
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const isSupplierUser = Boolean(currentUser?.departmentFlags?.isSupplier);
  const hasReportingAccess = Boolean(currentUser?.accesses?.includes("reporting"));
  const [language, setLanguage] = useState<"fr" | "kr">("fr");
  const catalogText = CATALOG_I18N[language];
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [storages, setStorages] = useState<Storage[]>([]);
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);
  const [mappingFields, setMappingFields] = useState<CatalogFieldMeta[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvText, setCsvText] = useState("");
  const [mappingSelection, setMappingSelection] = useState<Record<string, string | null>>({});
  const [importLoading, setImportLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [stockImportOpen, setStockImportOpen] = useState(false);
  const [stockImportStorageId, setStockImportStorageId] = useState<string>();
  const [stockImportReport, setStockImportReport] = useState<ParsedStockReport | null>(null);
  const [stockImportLoading, setStockImportLoading] = useState(false);
  const [inventoryLogsOpen, setInventoryLogsOpen] = useState(false);
  const [inventoryLogsLoading, setInventoryLogsLoading] = useState(false);
  const [inventoryLogs, setInventoryLogs] = useState<InventoryLog[]>([]);
  const [inventoryLogStorageId, setInventoryLogStorageId] = useState<string>();
  const [readonlyProduct, setReadonlyProduct] = useState<CatalogItem | null>(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [exportFormat, setExportFormat] = useState<"csv" | "json">("csv");
  const [exportRowScope, setExportRowScope] = useState<"all" | "selected">("all");
  const [exportColumns, setExportColumns] = useState<Array<{ key: string; label: string; selected: boolean }>>([
    { key: "title", label: "Nom produit", selected: true },
    { key: "handle", label: "Handle", selected: true },
    { key: "vendor", label: "Marque", selected: true },
    { key: "type", label: "Type", selected: true },
    { key: "status", label: "Statut", selected: true },
    { key: "tags", label: "Tags", selected: false },
    { key: "bodyHtml", label: "Description HTML", selected: false },
    { key: "Sku", label: "SKU", selected: true },
    { key: "EAN", label: "EAN", selected: true },
    { key: "Grams", label: "Poids (g)", selected: false },
    { key: "Price", label: "Prix", selected: true },
    { key: "costPerItem", label: "Coût unitaire", selected: false },
    { key: "WeightUnit", label: "Unité de poids", selected: false },
    { key: "imageSrc", label: "URL image", selected: false },
    { key: "descriptorsSubtitle", label: "Sous-titre", selected: false },
    { key: "ShortDescription", label: "Description courte", selected: false },
    { key: "UseInstruction", label: "Conseils d'utilisation", selected: false },
    { key: "Disclaimer", label: "Disclaimer", selected: false },
    { key: "Faq", label: "FAQ", selected: false },
    { key: "Inci", label: "INCI", selected: false },
    { key: "Contenance", label: "Contenance", selected: false },
    { key: "Ingredients", label: "Ingrédients", selected: false },
    { key: "Preoccupations", label: "Préoccupations", selected: false },
    { key: "Typo", label: "Typologies", selected: false },
    { key: "Kitcomposition", label: "Kit composition", selected: false },
    { key: "FoundersChoice", label: "Founders choice", selected: false },
    { key: "customScoreYuka", label: "Score Yuka", selected: false },
    { key: "Batch", label: "Batch", selected: false },
  ]);

  const fetchCatalog = useCallback(async (query?: string) => {
    try {
      setLoading(true);
      const [catalogData, storageData, retailerData] = await Promise.all([
        getCatalog(query),
        isSupplierUser ? Promise.resolve([] as Storage[]) : getStorages(),
        isSupplierUser ? Promise.resolve([] as Retailer[]) : getRetailers(),
      ]);
      setItems(catalogData);
      setStorages(storageData);
      setRetailers(retailerData);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      message.error(`Impossible de charger les données: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [isSupplierUser]);

  const openInventoryLogs = async () => {
    try {
      setInventoryLogsLoading(true);
      setInventoryLogsOpen(true);
      setInventoryLogs(await getAllInventoryLogs());
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      message.error(`Impossible de charger les logs: ${msg}`);
    } finally {
      setInventoryLogsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const parseCsvLine = (line: string, separator: string) => {
    const values: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      const next = line[i + 1];

      if (char === '"') {
        if (inQuotes && next === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (char === separator && !inQuotes) {
        values.push(current);
        current = "";
        continue;
      }

      current += char;
    }

    values.push(current);
    return values.map((value) => value.trim());
  };

  const parseCsvHeaders = (text: string): string[] => {
    if (!text) return [];
    const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const firstLine = normalized.split("\n").find((line) => line.trim() !== "");
    if (!firstLine) return [];
    const separator = firstLine.includes(";") ? ";" : ",";
    return parseCsvLine(firstLine, separator);
  };

  const ensureMappingLoaded = useCallback(async () => {
    if (mappingFields.length > 0) return mappingFields;
    const response = await getCatalogMapping();
    setMappingFields(response.fields || []);
    return response.fields || [];
  }, [mappingFields.length]);

  const handleFileSelected = async (file: File) => {
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = String(event.target?.result || "");
      const headers = parseCsvHeaders(text);
      setCsvText(text);
      setCsvHeaders(headers);

      try {
        const fields = await ensureMappingLoaded();
        const defaultMapping: Record<string, string | null> = {};
        fields.forEach((field) => {
          if (field.csvField && headers.includes(field.csvField)) {
            defaultMapping[field.key] = field.csvField;
          } else {
            defaultMapping[field.key] = null;
          }
        });
        setMappingSelection(defaultMapping);
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Erreur inconnue";
        message.error(`Impossible de charger le mapping: ${msg}`);
      }
    };
    reader.readAsText(file);
  };

  const selectedStockStorage = useMemo(
    () => storages.find((storage) => String(storage.id || storage._id) === stockImportStorageId),
    [storages, stockImportStorageId]
  );

  const stockImportData = useMemo(() => {
    if (!stockImportReport || !selectedStockStorage?.stockImportTemplate) {
      return { headers: [] as string[], rows: [] as unknown[][] };
    }
    const template = selectedStockStorage.stockImportTemplate;
    const sheetName =
      stockImportReport.sheets.find((sheet) => sheet.name === template.sheetName)?.name ||
      stockImportReport.sheets[0]?.name ||
      "";
    return getStockReportDataRows(
      stockImportReport,
      sheetName,
      Number(template.headerRow || 1)
    );
  }, [selectedStockStorage, stockImportReport]);

  const handleStockReportSelected = async (file: File) => {
    try {
      setStockImportReport(await parseStockReportFile(file));
      message.success("Rapport chargé, vérifie l'aperçu avant import");
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Fichier illisible";
      message.error(msg);
    }
    return false;
  };

  const handleStockImportConfirm = async () => {
    if (!selectedStockStorage || !stockImportReport) {
      message.warning("Choisis un entrepôt et un rapport de stock");
      return;
    }
    const template = selectedStockStorage.stockImportTemplate;
    if (
      !template?.mapping?.quantity ||
      !template.mapping.status ||
      !template.statusMappings?.length
    ) {
      message.warning("Cet entrepôt n'a pas encore de template d'import configuré");
      return;
    }
    try {
      setStockImportLoading(true);
      const result = await importStockReport({
        storageId: String(selectedStockStorage.id || selectedStockStorage._id),
        headers: stockImportData.headers,
        rows: stockImportData.rows,
        fileName: stockImportReport.fileName,
      });
      message.success(`${result.updatedProducts} produits mis à jour`);
      if (result.unmatched.length > 0) {
        message.error(
          `${result.unmatched.length} ligne(s) n'ont pas mis à jour l'état du produit faute de SKU, EAN ou statut reconnu.`
        );
        Modal.warning({
          title: `${result.unmatched.length} états de stock non mis à jour`,
          content: (
            <div style={{ maxHeight: 280, overflowY: "auto" }}>
              {result.unmatched.slice(0, 30).map((row) => (
                <div key={`${row.row}-${row.productCode || row.barcode || "sans-identifiant"}`}>
                  Ligne {row.row}: {row.productCode || row.barcode || "sans identifiant"}
                  {row.reason === "status" ? ` - état non mappé : ${row.status || "vide"}` : ""}
                </div>
              ))}
            </div>
          ),
        });
      }
      setStockImportOpen(false);
      setStockImportReport(null);
      setStockImportStorageId(undefined);
      await fetchCatalog();
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      message.error(`Import impossible: ${msg}`);
    } finally {
      setStockImportLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Image",
        dataIndex: "imageSrc",
        key: "imageSrc",
        width: hasReportingAccess ? "6%" : "5%",
        render: (value: string | undefined, record: CatalogItem) => {
          const src = value || record.imageSrc;
          if (!src) return "-";
          return <Image src={src} alt={record.title || "Produit"} width={30} height={30} preview={false} style={{ objectFit: "cover", borderRadius: 6 }} />;
        },
      },
      {
        title: isSupplierUser ? catalogText.product : "Produit",
        dataIndex: "title",
        key: "title",
        width: hasReportingAccess ? "31%" : "28%",
        ellipsis: true,
        render: (value: string, record: CatalogItem) => (
          <Space direction="vertical" size={0} style={{ width: "100%", minWidth: 0 }}>
            <Text strong ellipsis style={{ maxWidth: "100%", display: "block", whiteSpace: "nowrap" }}>
              {value || record.handle || "Sans titre"}
            </Text>
            {record.handle ? (
              <Text type="secondary" ellipsis style={{ maxWidth: "100%", display: "block", whiteSpace: "nowrap" }}>
                {record.handle}
              </Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: isSupplierUser ? catalogText.brand : "Marque",
        dataIndex: "vendor",
        key: "vendor",
        width: hasReportingAccess ? "10%" : "12%",
        ellipsis: true,
      },
      {
        title: "SKU",
        dataIndex: "Sku",
        key: "Sku",
        width: hasReportingAccess ? "13%" : "10%",
        ellipsis: true,
      },
      {
        title: "Prix",
        dataIndex: "Price",
        key: "Price",
        width: hasReportingAccess ? "8%" : "8%",
        render: (value: number | undefined) => (typeof value === "number" ? `${value.toFixed(2)} €` : "-"),
      },
      {
        title: catalogText.totalStockShort,
        key: "totalStock",
        width: hasReportingAccess ? "8%" : "7%",
        render: (_: unknown, record: CatalogItem) => {
          const total = record.storageStock?.reduce((sum, item) => 
            sum + (item.sellableQuantity || 0) + (item.reservedQuantity || 0) + (item.defectiveQuantity || 0), 0) || 0;
          return <Text strong style={{ color: total > 0 ? '#10b981' : '#ef4444' }}>{total}</Text>;
        }
      },
      {
        title: catalogText.stockShort,
        key: "warehouseStock",
        width: "18%",
        render: (_: unknown, record: CatalogItem) => (
          <Space direction="vertical" size={3} style={{ width: "100%", minWidth: 0 }}>
            {(record.storageStock || []).map((stock) => (
              <div key={String(stock.storageId)} className="stock-cell-line">
                <Text strong ellipsis className="stock-cell-name">{stock.storageName || "Entrepôt"}</Text>
                <div className="stock-cell-values">
                  <span>{catalogText.sellableShort} {stock.sellableQuantity || 0}</span>
                  <span>{catalogText.reservedShort} {stock.reservedQuantity || 0}</span>
                  <span>{catalogText.defectiveShort} {stock.defectiveQuantity || 0}</span>
                </div>
              </div>
            ))}
          </Space>
        ),
      },
      {
        title: isSupplierUser ? catalogText.status : "Statut",
        dataIndex: "status",
        key: "status",
        width: hasReportingAccess ? "10%" : "8%",
        render: (status: string) => {
          const colors: Record<string, string> = {
            active: 'green',
            draft: 'orange',
            archived: 'red'
          };
          const labels: Record<string, string> = {
            active: language === "kr" && isSupplierUser ? "활성" : "Actif",
            draft: language === "kr" && isSupplierUser ? "초안" : "Brouillon",
            archived: language === "kr" && isSupplierUser ? "보관됨" : "Archivé"
          };
          return (
            <Tag color={colors[status] || 'default'} style={{ borderRadius: 6, fontWeight: 600, textTransform: 'uppercase', fontSize: 10, border: 'none' }}>
              {labels[status] || status || 'Inconnu'}
            </Tag>
          );
        }
      },
      {
        title: isSupplierUser ? catalogText.updated : "Mise à jour",
        dataIndex: "updatedAt",
        key: "updatedAt",
        width: hasReportingAccess ? "14%" : "10%",
        render: (value: string | undefined) =>
          value ? new Date(value).toLocaleDateString("fr-FR") : "-",
      },
      {
        title: "Actions",
        key: "actions",
        width: hasReportingAccess ? "8%" : "12%",
        render: (_: unknown, record: CatalogItem) =>
          isSupplierUser ? (
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => setReadonlyProduct(record)}
            >
              Voir les lots
            </Button>
          ) : (
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
              setEditingItem(record);
              
              // Préparer le stock par entrepôt pour inclure tous les entrepôts existants
              const currentStock = record.storageStock || [];
              const initializedStock = storages.map(storage => {
                const storageId = String(storage.id || storage._id);
                const existing = currentStock.find(s => String(s.storageId) === storageId);
                return {
                  storageId: storageId,
                  storageName: storage.name,
                  sellableQuantity: existing?.sellableQuantity || 0,
                  reservedQuantity: existing?.reservedQuantity || 0,
                  defectiveQuantity: existing?.defectiveQuantity || 0,
                  totalQuantity: (existing?.sellableQuantity || 0) + (existing?.reservedQuantity || 0) + (existing?.defectiveQuantity || 0)
                };
              });

              form.setFieldsValue({
                ...record,
                Ingredients: record.Ingredients || [],
                Preoccupations: record.Preoccupations || [],
                Typo: record.Typo || [],
                storageStock: initializedStock,
                distributorPrices: record.distributorPrices || [],
                compliance: record.compliance || { certification: [] },
              });
              setFormOpen(true);
            }}
            >
              Modifier
            </Button>
          ),
      },
    ].filter((column) => {
      if (String(column.key) === "warehouseStock") return hasReportingAccess;
      if (hasReportingAccess && ["actions", "Price", "vendor"].includes(String(column.key))) return false;
      return true;
    }),
    [catalogText, form, hasReportingAccess, isSupplierUser, storages]
  );

  const getRowKey = (record: CatalogItem) =>
    record.id || record._id || record.handle || Math.random().toString(36);

  const exportCsv = () => {
    if (!items.length) {
      message.warning("Aucun produit à exporter.");
      return;
    }
    const selectedColumns = exportColumns.filter((col) => col.selected);
    if (!selectedColumns.length) {
      message.warning("Sélectionne au moins une colonne à exporter.");
      return;
    }

    const exportRows =
      exportRowScope === "selected" && selectedRowKeys.length
        ? items.filter((item) => selectedRowKeys.includes(getRowKey(item)))
        : items;

    if (!exportRows.length) {
      message.warning("Aucune ligne à exporter pour ce filtre.");
      return;
    }

    if (exportFormat === "json") {
      const jsonRows = exportRows.map((item) =>
        selectedColumns.reduce<Record<string, unknown>>((acc, col) => {
          acc[col.label || col.key] = (item as Record<string, unknown>)[col.key] ?? "";
          return acc;
        }, {})
      );
      const blob = new Blob([JSON.stringify(jsonRows, null, 2)], {
        type: "application/json;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "catalogue.json";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      return;
    }

    const headers = selectedColumns.map((col) => col.label || col.key);
    const rows = exportRows.map((item) =>
      selectedColumns.map((col) => {
        const value = (item as Record<string, unknown>)[col.key];
        return value ?? "";
      })
    );

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(";"))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "catalogue.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleImportConfirm = async () => {
    if (!selectedFile || !csvText) {
      message.warning("Sélectionne un fichier CSV à importer.");
      return;
    }
    try {
      setImportLoading(true);
      const mapping = mappingSelection || {};
      const result = await importCatalogCsv({ csv: csvText, mapping });
      message.success(
        `Import terminé: ${result.inserted} ajoutés, ${result.updated} mis à jour, ${result.ignored} ignorés.`
      );
      setImportOpen(false);
      setSelectedFile(null);
      setCsvHeaders([]);
      setCsvText("");
      await fetchCatalog(search);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      message.error(`Import impossible: ${msg}`);
    } finally {
      setImportLoading(false);
    }
  };

  const handleSaveProduct = async () => {
    try {
      const values = await form.validateFields();
      
      // Calculer le total par entrepôt avant sauvegarde
      if (values.storageStock && Array.isArray(values.storageStock)) {
        values.storageStock = values.storageStock.map((s: any) => ({
          ...s,
          sellableQuantity: s.sellableQuantity || 0,
          reservedQuantity: s.reservedQuantity || 0,
          defectiveQuantity: s.defectiveQuantity || 0,
          totalQuantity: (s.sellableQuantity || 0) + (s.reservedQuantity || 0) + (s.defectiveQuantity || 0)
        }));
      }

      setFormLoading(true);
      if (editingItem?.id || editingItem?._id) {
        const id = String(editingItem.id || editingItem._id);
        await updateCatalogItem(id, values);
        message.success("Produit mis à jour.");
      } else {
        await createCatalogItem(values);
        message.success("Produit créé.");
      }
      setFormOpen(false);
      setEditingItem(null);
      form.resetFields();
      await fetchCatalog(search);
    } catch (error) {
      if (error instanceof Error && error.message.includes("validation")) return;
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      message.error(`Impossible d'enregistrer: ${msg}`);
    } finally {
      setFormLoading(false);
    }
  };

  const stats = useMemo(() => {
    const total = items.length;
    const marketableStock = items.reduce(
      (sum, item) =>
        sum +
        (item.storageStock || []).reduce(
          (stockSum, stock) => stockSum + Number(stock.sellableQuantity || 0),
          0
        ),
      0
    );
    const vendors = new Set(items.map((i) => i.vendor).filter(Boolean)).size;
    const lots = new Set(
      items.flatMap((item) =>
        (item.storageStock || []).flatMap((stock) =>
          (stock.lots || []).map(
            (lot) => `${String(stock.storageId)}:${lot.reference}`
          )
        )
      )
    ).size;
    return { total, marketableStock, vendors, lots };
  }, [items]);

  const filteredItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesSearch =
        !normalizedSearch ||
        [
          item.title,
          item.handle,
          item.vendor,
          item.Sku,
          item.EAN,
          item.Barcode,
        ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
      return matchesSearch;
    });
  }, [items, search]);

  const filteredInventoryLogs = useMemo(
    () =>
      inventoryLogStorageId
        ? inventoryLogs.filter((log) => String(log.location) === inventoryLogStorageId)
        : inventoryLogs,
    [inventoryLogStorageId, inventoryLogs]
  );

  return (
    <div className="catalog-cockpit">
      <div className="display-flex">
        <div>
          <Title level={2}>{isSupplierUser ? catalogText.title : "Catalogue"}</Title>
          <Text type="secondary" style={{ marginBottom: 24, display: "block" }}>
            {isSupplierUser
              ? catalogText.supplierSubtitle
              : catalogText.internalSubtitle}
          </Text>
        </div>
        <Space>
          {isSupplierUser && (
            <div className="premium-lang-selector catalog-lang-selector mt-2 p-1 rounded-full flex items-center gap-1 backdrop-blur-sm">
              {(["fr", "kr"] as const).map((l) => (
                <Button
                  key={l}
                  size="small"
                  onClick={() => setLanguage(l)}
                  className={`w-8 h-8 flex items-center justify-center text-[10px] font-bold border-none rounded-full transition-all ${
                    language === l
                      ? "catalog-lang-btn-active shadow-sm"
                      : "catalog-lang-btn-idle"
                  }`}
                >
                  {l.toUpperCase()}
                </Button>
              ))}
            </div>
          )}
          {!isSupplierUser && <Button icon={<InboxOutlined />} onClick={() => setStockImportOpen(true)}>
            {catalogText.importStock}
          </Button>}
          {!isSupplierUser && (
            <Button icon={<HistoryOutlined />} onClick={openInventoryLogs}>
              {catalogText.inventoryLogs}
            </Button>
          )}
          {!isSupplierUser && <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
            {catalogText.importCatalog}
          </Button>}
          {!isSupplierUser && <Button icon={<DownloadOutlined />} onClick={() => setExportOpen(true)}>
            {catalogText.exportData}
          </Button>}
          {!isSupplierUser && <Button icon={<PlusOutlined />} type="primary" onClick={() => {
            setEditingItem(null);
            
            // Initialiser le stock pour tous les entrepôts existants pour un nouveau produit
            const initializedStock = storages.map(storage => {
              const storageId = String(storage.id || storage._id);
              return {
                storageId: storageId,
                storageName: storage.name,
                sellableQuantity: 0,
                reservedQuantity: 0,
                defectiveQuantity: 0,
                totalQuantity: 0
              };
            });

            form.resetFields();
            form.setFieldsValue({
              status: 'draft',
              storageStock: initializedStock,
              compliance: { certification: [] },
              Ingredients: [],
              Preoccupations: [],
              Typo: [],
            });
            setFormOpen(true);
          }}>
            {catalogText.addProduct}
          </Button>}
        </Space>
      </div>

      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} lg={8}>
          <Card className="neo-card" bodyStyle={{ padding: '24px' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {catalogText.products}
                </Text>
                <TeamOutlined style={{ color: '#64748b' }} />
              </div>
              <Title level={2} style={{ margin: 0, fontWeight: 800 }}>{stats.total}</Title>
              <div className="trend-up">{catalogText.catalogComplete}</div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className="neo-card" bodyStyle={{ padding: '24px' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {catalogText.marketableStockKpi}
                </Text>
                <InboxOutlined style={{ color: '#64748b' }} />
              </div>
              <Title level={2} style={{ margin: 0, fontWeight: 800 }}>{stats.marketableStock}</Title>
              <div className="trend-up">{catalogText.marketableStockSubtitle}</div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className="neo-card" bodyStyle={{ padding: '24px' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {hasReportingAccess ? catalogText.lots : catalogText.brandCount}
                </Text>
                <SafetyOutlined style={{ color: '#64748b' }} />
              </div>
              <Title level={2} style={{ margin: 0, fontWeight: 800 }}>
                {hasReportingAccess ? stats.lots : stats.vendors}
              </Title>
              <div className="trend-up">
                {hasReportingAccess ? catalogText.lotDetails : catalogText.brandDiversity}
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card className="neo-card" style={{ marginBottom: 24 }}>
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space size="middle" wrap>
              <Input
                placeholder={isSupplierUser ? catalogText.search : catalogText.internalSearch}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ width: 320, borderRadius: 8 }}
                prefix={<PlusOutlined style={{ transform: 'rotate(45deg)', color: '#94a3b8' }} />}
              />
              <Button 
                type="text" 
                icon={<ReloadOutlined />} 
                onClick={() => fetchCatalog()}
                loading={loading}
                style={{ color: '#64748b', fontWeight: 600 }}
              >
                {catalogText.refresh}
              </Button>
            </Space>
            {selectedRowKeys.length > 0 && (
              <Text strong style={{ color: '#0f172a' }}>
                {selectedRowKeys.length} {catalogText.selectedItems}
              </Text>
            )}
          </div>

          <div className="catalog-table-audit">
            <Table
              rowKey={getRowKey}
              columns={columns}
              dataSource={filteredItems}
              loading={loading}
              tableLayout="fixed"
              pagination={{
                pageSize: 12,
                showSizeChanger: false,
                position: ['bottomRight']
              }}
              rowSelection={isSupplierUser ? undefined : {
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                columnWidth: 20,
              }}
              expandable={isSupplierUser ? {
                columnWidth: 20,
                rowExpandable: (record) =>
                  (record.storageStock || []).some((stock) => (stock.lots || []).length > 0),
                expandedRowRender: (record) => (
                  <Card size="small" title={catalogText.lotDetails}>
                    {(record.storageStock || []).length === 0 ? (
                      <Text type="secondary">{catalogText.noLots}</Text>
                    ) : (
                      <Space direction="vertical" size={12} style={{ width: "100%" }}>
                        {(record.storageStock || []).map((stock) => (
                          <div key={String(stock.storageId)}>
                            <Text strong>{stock.storageName || "Entrepôt"}</Text>
                            {(stock.lots || []).length === 0 ? (
                              <div><Text type="secondary">{catalogText.noLots}</Text></div>
                            ) : (
                              <Table
                                size="small"
                                pagination={false}
                                rowKey={(lot) => `${lot.reference}-${String(lot.expiryDate || "")}`}
                                dataSource={stock.lots}
                                columns={[
                                  { title: catalogText.lot, dataIndex: "reference", key: "reference" },
                                  {
                                    title: catalogText.expiry,
                                    dataIndex: "expiryDate",
                                    key: "expiryDate",
                                    render: (value) => value ? new Date(value).toLocaleDateString(language === "kr" ? "ko-KR" : "fr-FR") : "-",
                                  },
                                  {
                                    title: catalogText.quantity,
                                    key: "states",
                                    render: (_, lot) => (
                                      <Space wrap>
                                        {(lot.statusQuantities || []).map((state) => (
                                          <Tag
                                            key={state.status}
                                            color={state.status === "marketable" ? "green" : state.status === "reserved" ? "gold" : "red"}
                                          >
                                            {catalogText[state.status]}: {state.quantity}
                                          </Tag>
                                        ))}
                                      </Space>
                                    ),
                                  },
                                ]}
                              />
                            )}
                          </div>
                        ))}
                      </Space>
                    )}
                  </Card>
                ),
              } : undefined}
              className="high-tech-table"
            />
          </div>
        </Space>
      </Card>

      <Modal
        title={catalogText.inventoryLogsTitle}
        open={inventoryLogsOpen}
        onCancel={() => setInventoryLogsOpen(false)}
        footer={null}
        width={900}
        styles={{ body: { padding: 24, maxHeight: "72vh", overflowY: "auto" } }}
      >
        <Select
          allowClear
          placeholder="Tous les entrepôts"
          value={inventoryLogStorageId}
          onChange={setInventoryLogStorageId}
          options={storages.map((storage) => ({
            value: String(storage.id || storage._id),
            label: storage.name,
          }))}
          style={{ width: 280, marginBottom: 20 }}
        />
        {inventoryLogsLoading ? (
          <Card loading />
        ) : filteredInventoryLogs.length === 0 ? (
          <Empty description="Aucun log d'inventaire" />
        ) : (
          <Collapse
            items={filteredInventoryLogs.map((log) => {
              const storage = storages.find(
                (item) => String(item.id || item._id) === String(log.location)
              );
              return {
                key: String(log._id || `${log.location}-${log.date}`),
                label: (
                  <Space>
                    <Text strong>
                      {new Date(log.date || log.createdAt || "").toLocaleString("fr-FR")}
                    </Text>
                    <Tag>{storage?.name || log.location}</Tag>
                    <Tag color="blue">{log.changes?.length || 0} produit(s)</Tag>
                  </Space>
                ),
                children: (
                  <Table
                    size="small"
                    pagination={false}
                    rowKey={(change) => `${change.id}-${change.sku || change.name}`}
                    dataSource={log.changes || []}
                    columns={[
                      {
                        title: "Produit",
                        key: "product",
                        render: (_, change) => (
                          <Space direction="vertical" size={0}>
                            <Text strong>{change.name}</Text>
                            <Text type="secondary">{change.sku || change.barcode || "-"}</Text>
                          </Space>
                        ),
                      },
                      {
                        title: "Variation totale",
                        dataIndex: "qte",
                        key: "qte",
                        render: (value) => (
                          <Tag color={Number(value) >= 0 ? "green" : "red"}>
                            {Number(value) > 0 ? "+" : ""}{value || 0}
                          </Tag>
                        ),
                      },
                      {
                        title: "Variations par état",
                        key: "states",
                        render: (_, change) => (
                          <Space wrap>
                            {(change.stateChanges || []).map((state) => (
                              <Tag key={state.status}>
                                {catalogText[state.status]}: {state.variation > 0 ? "+" : ""}
                                {state.variation}
                              </Tag>
                            ))}
                          </Space>
                        ),
                      },
                    ]}
                  />
                ),
              };
            })}
          />
        )}
      </Modal>

      <Modal
        title={readonlyProduct?.title || "Détail des lots"}
        open={Boolean(readonlyProduct)}
        onCancel={() => setReadonlyProduct(null)}
        footer={null}
        width={760}
        styles={{ body: { padding: 24 } }}
      >
        {readonlyProduct && (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {(readonlyProduct.storageStock || []).map((stock) => (
              <Card key={String(stock.storageId)} size="small" title={stock.storageName || "Entrepôt"}>
                {(stock.lots || []).length ? (
                  <Table
                    size="small"
                    pagination={false}
                    rowKey={(lot) => `${lot.reference}-${String(lot.expiryDate || "")}`}
                    dataSource={stock.lots}
                    columns={[
                      { title: catalogText.lot, dataIndex: "reference", key: "reference" },
                      {
                        title: catalogText.expiry,
                        dataIndex: "expiryDate",
                        key: "expiryDate",
                        render: (value) =>
                          value ? new Date(value).toLocaleDateString(language === "kr" ? "ko-KR" : "fr-FR") : "-",
                      },
                      {
                        title: catalogText.quantity,
                        key: "quantities",
                        render: (_, lot) => (
                          <Space wrap>
                            {(lot.statusQuantities || []).map((state) => (
                              <Tag key={state.status}>
                                {catalogText[state.status]}: {state.quantity}
                              </Tag>
                            ))}
                          </Space>
                        ),
                      },
                    ]}
                  />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={catalogText.noLots} />
                )}
              </Card>
            ))}
          </Space>
        )}
      </Modal>

      <Modal
        title={catalogText.importStockTitle}
        open={stockImportOpen}
        onCancel={() => {
          setStockImportOpen(false);
          setStockImportReport(null);
          setStockImportStorageId(undefined);
        }}
        onOk={handleStockImportConfirm}
        okText="Mettre à jour le stock"
        confirmLoading={stockImportLoading}
        okButtonProps={{
          disabled:
            !stockImportReport ||
            !selectedStockStorage?.stockImportTemplate?.mapping?.quantity ||
            !selectedStockStorage?.stockImportTemplate?.mapping?.status ||
            !selectedStockStorage?.stockImportTemplate?.statusMappings?.length,
        }}
        width={920}
        destroyOnClose
        className="neo-modal"
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Form layout="vertical">
            <Form.Item label="Entrepôt source" required>
              <Select
                showSearch
                value={stockImportStorageId}
                onChange={(value) => {
                  setStockImportStorageId(value);
                  setStockImportReport(null);
                }}
                options={storages.map((storage) => ({
                  value: String(storage.id || storage._id),
                  label:
                    storage.stockImportTemplate?.mapping?.quantity &&
                    storage.stockImportTemplate?.mapping?.status &&
                    storage.stockImportTemplate?.statusMappings?.length
                    ? storage.name
                    : `${storage.name} - template non configuré`,
                }))}
                placeholder="Choisir l'entrepôt"
              />
            </Form.Item>
          </Form>

          {selectedStockStorage && (
            !selectedStockStorage.stockImportTemplate?.mapping?.quantity ||
            !selectedStockStorage.stockImportTemplate?.mapping?.status ||
            !selectedStockStorage.stockImportTemplate?.statusMappings?.length
          ) && (
            <Card size="small">
              <Text type="warning">
                Configure d'abord le template d'import de cet entrepôt dans Logistique &gt; Entrepôts.
              </Text>
            </Card>
          )}

          {selectedStockStorage?.stockImportTemplate?.mapping?.quantity &&
            selectedStockStorage.stockImportTemplate.mapping.status &&
            selectedStockStorage.stockImportTemplate.statusMappings?.length && (
            <>
              <Card size="small" title="Mapping utilisé">
                <Space direction="vertical" size={8}>
                  <Space wrap>
                    {Object.entries(selectedStockStorage.stockImportTemplate.mapping || {})
                      .filter(([, header]) => header)
                      .map(([field, header]) => (
                        <Tag key={field}>{field}: {header}</Tag>
                      ))}
                  </Space>
                  <Space wrap>
                    {selectedStockStorage.stockImportTemplate.statusMappings.map((statusMapping) => (
                      <Tag color="blue" key={`${statusMapping.externalValue}-${statusMapping.internalStatus}`}>
                        {statusMapping.externalValue} → {statusMapping.internalStatus}
                      </Tag>
                    ))}
                  </Space>
                </Space>
              </Card>
              <Dragger
                multiple={false}
                maxCount={1}
                accept=".xlsx,.xls,.csv"
                beforeUpload={handleStockReportSelected}
                onRemove={() => setStockImportReport(null)}
                fileList={stockImportReport ? [{ uid: "-1", name: stockImportReport.fileName } as any] : []}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Dépose le rapport réel de l'entrepôt</p>
                <p className="ant-upload-hint">Formats supportés: XLSX, XLS, CSV</p>
              </Dragger>
            </>
          )}

          {stockImportReport && stockImportData.headers.length > 0 && (
            <Card size="small" title={`Aperçu - ${stockImportData.rows.length} lignes`}>
              <Table
                size="small"
                pagination={false}
                scroll={{ x: true }}
                dataSource={stockImportData.rows.slice(0, 8).map((row, index) => ({
                  key: index,
                  row,
                }))}
                columns={Object.entries(selectedStockStorage?.stockImportTemplate?.mapping || {})
                  .filter(([, header]) => header && stockImportData.headers.includes(header))
                  .map(([field, header]) => ({
                    title: field,
                    key: field,
                    render: (_: unknown, record: { row: unknown[] }) => {
                      const index = stockImportData.headers.indexOf(String(header));
                      const value = record.row[index];
                      return String(value ?? "");
                    },
                  }))}
              />
            </Card>
          )}
        </Space>
      </Modal>

      <Modal
        title={catalogText.importCatalogTitle}
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        okText={catalogText.importCatalog}
        onOk={handleImportConfirm}
        okButtonProps={{ disabled: !selectedFile || importLoading, loading: importLoading }}
        destroyOnClose
        width={900}
        className="neo-modal"
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Text type="secondary">
            Dépose un fichier CSV exporté depuis votre outil (Shopify, ERP...). Le mapping des colonnes sera appliqué automatiquement.
          </Text>
          <Dragger
            multiple={false}
            accept=".csv"
            beforeUpload={(file) => {
              handleFileSelected(file);
              return false;
            }}
            onRemove={() => {
              setSelectedFile(null);
              setCsvHeaders([]);
              setCsvText("");
              setMappingSelection({});
            }}
            fileList={selectedFile ? [selectedFile as any] : []}
            className="neo-dragger"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: '#38bdf8' }} />
            </p>
            <p className="ant-upload-text">Glisse un CSV ici ou clique pour sélectionner</p>
            <p className="ant-upload-hint">Formats supportés: .csv</p>
          </Dragger>
          {csvHeaders.length > 0 ? (
            <Card size="small" title="Mapping des colonnes" style={{ marginTop: 8 }} className="neo-card">
              <Table
                rowKey={(record) => record.key}
                pagination={false}
                size="small"
                dataSource={mappingFields}
                columns={[
                  {
                    title: "Champ catalogue",
                    dataIndex: "key",
                    key: "key",
                  },
                  {
                    title: "CSV par défaut",
                    dataIndex: "csvField",
                    key: "csvField",
                    render: (value: string | null) => value || "-",
                  },
                  {
                    title: "Colonne CSV",
                    dataIndex: "key",
                    key: "mapping",
                    render: (key: string) => (
                      <Select
                        style={{ minWidth: 200 }}
                        placeholder="Ignorer"
                        allowClear
                        value={mappingSelection[key] || undefined}
                        options={csvHeaders.map((header) => ({ value: header, label: header }))}
                        onChange={(value) =>
                          setMappingSelection((prev) => ({ ...prev, [key]: value || null }))
                        }
                      />
                    ),
                  },
                ]}
              />
            </Card>
          ) : null}
        </Space>
      </Modal>

      <Modal
        title={catalogText.exportCatalogTitle}
        open={exportOpen}
        onCancel={() => setExportOpen(false)}
        okText="Télécharger"
        onOk={exportCsv}
        destroyOnClose
        className="neo-modal"
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Title level={5} style={{ margin: 0 }}>
            Export catalogue
          </Title>
          <Text type="secondary">Personnalise les colonnes, les titres et le format avant export.</Text>
          <Divider style={{ margin: "4px 0" }} />
          <Space direction="vertical" size={6}>
            <Text strong>Format</Text>
            <Radio.Group
              value={exportFormat}
              onChange={(event) => setExportFormat(event.target.value)}
              options={[
                { label: "CSV", value: "csv" },
                { label: "JSON", value: "json" },
              ]}
            />
          </Space>
          <Divider style={{ margin: "4px 0" }} />
          <Space direction="vertical" size={6}>
            <Text strong>Lignes exportées</Text>
            <Radio.Group
              value={exportRowScope}
              onChange={(event) => setExportRowScope(event.target.value)}
              options={[
                { label: `Tout (${items.length})`, value: "all" },
                { label: `Sélection (${selectedRowKeys.length})`, value: "selected" },
              ]}
            />
          </Space>
          <Divider style={{ margin: "4px 0" }} />
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Text strong>Colonnes & renommage</Text>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {exportColumns.map((col) => (
                <Space key={col.key} style={{ width: "100%", marginBottom: 8 }} align="center">
                  <Checkbox
                    checked={col.selected}
                    onChange={(event) =>
                      setExportColumns((prev) =>
                        prev.map((c) =>
                          c.key === col.key ? { ...c, selected: event.target.checked } : c
                        )
                      )
                    }
                  >
                    {col.key}
                  </Checkbox>
                  <Input
                    size="small"
                    value={col.label}
                    onChange={(event) =>
                      setExportColumns((prev) =>
                        prev.map((c) =>
                          c.key === col.key ? { ...c, label: event.target.value } : c
                        )
                      )
                    }
                    placeholder="Nom de colonne"
                    style={{ width: 200 }}
                  />
                </Space>
              ))}
            </div>
          </Space>
        </Space>
      </Modal>

      <Modal
        title={editingItem ? "Modifier un produit" : "Créer un produit"}
        open={formOpen}
        onCancel={() => {
          setFormOpen(false);
          setEditingItem(null);
        }}
        okText={editingItem ? "Mettre à jour" : "Créer"}
        onOk={handleSaveProduct}
        confirmLoading={formLoading}
        width={960}
        destroyOnClose
        className="neo-modal"
        footer={[
          editingItem && (
            <Button
              key="delete"
              danger
              onClick={() => {
                Modal.confirm({
                  title: "Supprimer ce produit ?",
                  content: "Cette action est irréversible. Le produit sera définitivement supprimé du catalogue.",
                  okText: "Supprimer",
                  okType: "danger",
                  cancelText: "Annuler",
                  onOk: async () => {
                    try {
                      const itemId = editingItem._id || editingItem.id;
                      if (!itemId) {
                        message.error("ID du produit introuvable");
                        return;
                      }
                      const { deleteCatalogItem } = await import("../services/catalogApi");
                      await deleteCatalogItem(itemId);
                      message.success("Produit supprimé");
                      setFormOpen(false);
                      setEditingItem(null);
                      await fetchCatalog(search);
                    } catch (error) {
                      console.error("Erreur lors de la suppression:", error);
                      message.error("Erreur lors de la suppression du produit");
                    }
                  },
                });
              }}
              style={{ float: 'left' }}
            >
              Supprimer
            </Button>
          ),
          <Button key="cancel" onClick={() => { setFormOpen(false); setEditingItem(null); }}>
            Annuler
          </Button>,
          <Button key="submit" type="primary" loading={formLoading} onClick={handleSaveProduct}>
            {editingItem ? "Mettre à jour" : "Créer"}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Tabs
            className="neo-tabs"
            items={[
              {
                key: "core",
                label: "Essentiel",
                children: (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item label="Nom produit" name="title" rules={[{ required: true, message: "Nom requis" }]}
                      >
                        <Input placeholder="Nom du produit" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Handle" name="handle">
                        <Input placeholder="slug-produit" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Marque" name="vendor">
                        <Input placeholder="Marque" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Type" name="type">
                        <Input placeholder="Type" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Statut" name="status">
                        <Select
                          placeholder="Statut"
                          options={[
                            { value: "active", label: "Actif" },
                            { value: "draft", label: "Brouillon" },
                            { value: "archived", label: "Archivé" },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="SKU" name="Sku">
                        <Input placeholder="Référence interne" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="EAN"
                        name="EAN"
                        rules={[
                          {
                            pattern: /^\d{8,14}$/,
                            message: "L'EAN doit contenir entre 8 et 14 chiffres",
                          },
                        ]}
                      >
                        <Input inputMode="numeric" placeholder="Code EAN" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Tags" name="tags">
                        <Input placeholder="Tag1, Tag2" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Description HTML" name="bodyHtml">
                        <Input.TextArea rows={4} placeholder="Description détaillée" />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: "pricing",
                label: "Prix & stocks",
                children: (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item label="Poids (g)" name="Grams">
                        <InputNumber min={0} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Prix Retail" name="Price">
                        <InputNumber min={0} step={0.01} style={{ width: "100%" }} addonAfter="€" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Coût unitaire" name="costPerItem">
                        <InputNumber min={0} step={0.01} style={{ width: "100%" }} addonAfter="€" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Unité de poids" name="WeightUnit">
                        <Input placeholder="g, ml" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="URL image" name="imageSrc">
                        <Input placeholder="https://..." />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <Divider orientation="left">Stock par Entrepôt</Divider>
                      <Form.List name="storageStock">
                        {(fields) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => {
                              const storageName = form.getFieldValue(['storageStock', name, 'storageName']);
                              return (
                                <Card key={key} size="small" title={<Text strong>{storageName || "Entrepôt inconnu"}</Text>} style={{ marginBottom: 16, background: '#f8fafc' }}>
                                  <Form.Item {...restField} name={[name, 'storageId']} hidden><Input /></Form.Item>
                                  <Form.Item {...restField} name={[name, 'storageName']} hidden><Input /></Form.Item>
                                  <Row gutter={16}>
                                    <Col span={8}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'sellableQuantity']}
                                        label="Commercialisable"
                                        initialValue={0}
                                      >
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                      </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'reservedQuantity']}
                                        label="Réservé"
                                        initialValue={0}
                                      >
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                      </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'defectiveQuantity']}
                                        label="Défectueux"
                                        initialValue={0}
                                      >
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </Card>
                              );
                            })}
                            {fields.length === 0 && (
                              <div style={{ textAlign: 'center', padding: '20px', background: '#f8fafc', borderRadius: 8 }}>
                                <Text type="secondary">Aucun entrepôt configuré. Gérez les entrepôts dans le module Supply.</Text>
                              </div>
                            )}
                          </>
                        )}
                      </Form.List>
                    </Col>

                    <Col xs={24}>
                      <Divider orientation="left">Prix de Revente Distributeurs</Divider>
                      <Form.List name="distributorPrices">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <Row key={key} gutter={16} align="middle" style={{ marginBottom: 8 }}>
                                <Col span={10}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'distributorId']}
                                    rules={[{ required: true, message: 'Distributeur requis' }]}
                                    noStyle
                                  >
                                    <Select 
                                      placeholder="Sélectionner un distributeur"
                                      onChange={(val) => {
                                        const retailer = retailers.find(r => r.id === val || r._id === val);
                                        if (retailer) {
                                          const prices = form.getFieldValue('distributorPrices');
                                          prices[name].distributorName = retailer.name;
                                          form.setFieldsValue({ distributorPrices: prices });
                                        }
                                      }}
                                    >
                                      {retailers.map(r => (
                                        <Select.Option key={r.id || r._id} value={r.id || r._id}>{r.name}</Select.Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                  <Form.Item {...restField} name={[name, 'distributorName']} hidden><Input /></Form.Item>
                                </Col>
                                <Col span={10}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'price']}
                                    rules={[{ required: true, message: 'Prix requis' }]}
                                    noStyle
                                  >
                                    <InputNumber placeholder="Prix de revente" style={{ width: '100%' }} addonAfter="€" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Button type="link" danger onClick={() => remove(name)}>Supprimer</Button>
                                </Col>
                              </Row>
                            ))}
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                              Ajouter un prix distributeur
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Col>
                  </Row>
                ),
              },
              {
                key: "compliance",
                label: "Conformité",
                children: (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item label="Numéro CPNP" name={["compliance", "cpnpNumber"]}>
                        <Input placeholder="Ex: 1234567" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Lien DIC" name={["compliance", "dicUrl"]}>
                        <Input placeholder="https://..." />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Fiche technique (URL)" name={["compliance", "technicalSheetUrl"]}>
                        <Input placeholder="https://..." />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Code Formule" name={["compliance", "formulaCode"]}>
                        <Input placeholder="Ex: F-2024-001" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Code SH" name={["compliance", "formulaCode"]}>
                        <Input placeholder="Ex: F-2024-001" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Personne Responsable" name={["compliance", "responsiblePerson"]}>
                        <Input placeholder="Nom de l'entité" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="PAO (Période après ouverture)" name={["compliance", "paO"]}>
                        <Input placeholder="Ex: 12M" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Adresse Personne Responsable" name={["compliance", "responsiblePersonAddress"]}>
                        <Input.TextArea rows={2} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Certifications" name={["compliance", "certification"]}>
                        <Select mode="tags" placeholder="Bio, Vegan, etc." />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="VEA (Évaluation environnementale)" name={["compliance", "isVea"]} valuePropName="checked">
                        <Checkbox>Voluntary Environmental Assessment</Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: "marketing",
                label: "Marketing",
                children: (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item label="Sous-titre" name="descriptorsSubtitle">
                        <Input placeholder="Sous-titre produit" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Description courte" name="ShortDescription">
                        <Input placeholder="Description courte" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Conseils d'utilisation" name="UseInstruction">
                        <Input.TextArea rows={3} />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Disclaimer" name="Disclaimer">
                        <Input.TextArea rows={2} />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="FAQ" name="Faq">
                        <Input.TextArea rows={3} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: "composition",
                label: "Composition",
                children: (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item label="INCI" name="Inci">
                        <Input placeholder="INCI" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Contenance" name="Contenance">
                        <Input placeholder="250ml" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Ingrédients" name="Ingredients">
                        <Select mode="tags" placeholder="Ajouter des ingrédients" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Préoccupations" name="Preoccupations">
                        <Select mode="tags" placeholder="Hydratation, anti-âge..." />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Typologies" name="Typo">
                        <Select mode="tags" placeholder="Peau sèche, mixte..." />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Kit composition" name="Kitcomposition">
                        <Input placeholder="Kit composition" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Founders choice" name="FoundersChoice">
                        <Input placeholder="Oui/Non" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Score Yuka" name="customScoreYuka">
                        <InputNumber min={0} max={100} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
            ]}
          />
        </Form>
      </Modal>

      <style>{`
        .catalog-cockpit {
          padding: 24px;
          min-height: 100vh;
          background: #f8fafc;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        .display-flex {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 32px;
        }
        .display-flex h2 {
          margin: 0 !important;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: #0f172a;
        }
        .neo-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .neo-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }
        .trend-up { color: #10b981; font-weight: 600; font-size: 13px; }
        .catalog-lang-selector {
          background: #0f172a;
        }
        .catalog-lang-selector .ant-btn {
          color: #ffffff !important;
        }
        .catalog-lang-selector .catalog-lang-btn-active {
          background: #1e293b !important;
          color: #ffffff !important;
        }
        .catalog-lang-selector .catalog-lang-btn-idle {
          background: transparent !important;
          color: rgba(255, 255, 255, 0.72) !important;
        }
        .catalog-lang-selector .catalog-lang-btn-idle:hover {
          background: rgba(255, 255, 255, 0.18) !important;
          color: #ffffff !important;
        }
        .high-tech-table .ant-table-wrapper,
        .high-tech-table .ant-spin-nested-loading,
        .high-tech-table .ant-spin-container,
        .high-tech-table .ant-table,
        .high-tech-table .ant-table-container,
        .high-tech-table .ant-table-content {
          max-width: 100% !important;
          width: 100% !important;
        }
        .high-tech-table .ant-table-cell {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .high-tech-table .ant-table-selection-column {
          width: 20px !important;
          min-width: 20px !important;
          max-width: 20px !important;
          padding-left: 2px !important;
          padding-right: 2px !important;
          text-align: center !important;
        }
        .high-tech-table .ant-table-selection-column .ant-checkbox-wrapper {
          margin-inline-end: 0 !important;
        }
        .high-tech-table col.ant-table-selection-col {
          width: 20px !important;
        }
        .stock-cell-line {
          display: flex;
          flex-direction: column;
          gap: 3px;
          align-items: flex-start;
          width: 100%;
          min-width: 0;
        }
        .stock-cell-name {
          min-width: 0;
          max-width: 100%;
          font-size: 11px;
        }
        .stock-cell-values {
          display: flex;
          flex-direction: column;
          gap: 1px;
          font-size: 9px;
          line-height: 1.05;
          color: #64748b;
          white-space: nowrap;
        }
        
        /* Table Styles */
        .high-tech-table .ant-table {
          background: transparent !important;
          table-layout: fixed !important;
        }
        .high-tech-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #64748b !important;
          font-size: 10px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          border-bottom: 1px solid #e2e8f0 !important;
          padding: 6px 4px !important;
        }
        .high-tech-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
          padding: 14px 4px !important;
        }
        .high-tech-table .ant-table-tbody > tr:hover > td {
          background: #f1f5f9 !important;
        }
        
        /* Button Styles */
        .ant-btn-primary {
          background: #0f172a !important;
          border: none !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
          height: 40px !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }
        .ant-btn-primary:hover {
          background: #1e293b !important;
          transform: translateY(-1px);
        }
        .ant-btn:not(.ant-btn-primary) {
          border-radius: 8px !important;
          height: 40px !important;
          border-color: #e2e8f0 !important;
          font-weight: 600 !important;
        }

        /* Modal & Dragger */
        .neo-modal .ant-modal-content {
          border-radius: 16px !important;
          padding: 24px !important;
        }
        .neo-dragger {
          background: #f8fafc !important;
          border: 2px dashed #e2e8f0 !important;
          border-radius: 12px !important;
        }
        .neo-dragger:hover {
          border-color: #38bdf8 !important;
        }
        
        /* Tabs */
        .neo-tabs .ant-tabs-nav::before {
          border-bottom: 1px solid #e2e8f0 !important;
        }
        .neo-tabs .ant-tabs-tab-btn {
          font-weight: 600 !important;
          color: #64748b !important;
        }
        .neo-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #0f172a !important;
        }
        .neo-tabs .ant-tabs-ink-bar {
          background: #0f172a !important;
        }
      `}</style>
    </div>
  );
};

export default Catalog;
